import styled from 'styled-components';
import { brandTheme } from '../../../theme/brandTokens';

/**
 * Settings — visual port of the Remix reference's routes/settings.tsx.
 *
 * Same conventions as the Offers and Team ports: --hd-* tokens only, and the
 * reference's overridden radius scale (--radius: 0.875rem):
 *   sm 10 - md 12 - lg 14 - xl 18 - 2xl 22 - full 999
 * Font sizes use Tailwind's paired line heights (xs 12/16, sm 14/20,
 * base 16/24, 2xl 24/32, 3xl 30/36).
 *
 * Careful: a backtick inside a CSS comment ends the template literal early and
 * the module throws on load. Keep comments backtick-free.
 */

const FG = 'var(--hd-foreground)';
const MUTED_FG = 'var(--hd-muted-foreground)';
const MUTED = 'var(--hd-muted)';
const BORDER = 'var(--hd-border)';
const BG = 'var(--hd-background)';
const GRADIENT = 'var(--hd-gradient-houdiny)';

const MUTED_70 = 'color-mix(in srgb, var(--hd-muted) 70%, transparent)';
const FG_30 = 'color-mix(in srgb, var(--hd-foreground) 30%, transparent)';

export const StyledSettings = styled.div`
  ${brandTheme}
  min-height: 100%;
  background: ${BG};
  color: ${FG};

  /* The shared Switch is on in the app's old blue; version3's is the primary.
     The type attribute only makes this outrank the Switch's own rule. */
  input[type='checkbox']:checked + .switch {
    background: var(--hd-primary);
  }
  font-family: var(--base-font-sans-serif);
  -webkit-font-smoothing: antialiased;

  /* Reference: mx-auto max-w-3xl px-8 py-8. */
  .settings-main {
    max-width: 768px;
    margin: 0 auto;
    padding: 32px;

    @media (max-width: 767px) {
      padding: 32px 16px;
    }
  }

  /* Reference: mb-8; h1 text-2xl font-semibold tracking-tight; p mt-1 text-sm. */
  .settings-head {
    margin-bottom: 32px;
  }

  .settings-title {
    margin: 0;
    font-size: 24px;
    line-height: 32px;
    font-weight: 600;
    letter-spacing: -0.025em;
    /* GlobalStyles capitalises every heading — the reference does not. */
    text-transform: none;
    color: ${FG};
  }

  .settings-desc {
    margin: 4px 0 0;
    font-size: 14px;
    line-height: 20px;
    color: ${MUTED_FG};
  }

  .settings-section {
    margin-bottom: 48px;
    /* Opened at a section (/profile#subscription), its title clears the app's top bar. */
    scroll-margin-top: 88px;

    &.is-last {
      margin-bottom: 64px;
    }

    /* Not built yet. The card is dimmed and made inert; the heading keeps its
       full contrast so the badge beside it is the first thing read rather than
       something the user has to squint at. */
    &.is-soon .settings-card {
      opacity: 0.55;
      pointer-events: none;
      user-select: none;
    }
  }

  .section-soon {
    display: inline-block;
    margin-left: 8px;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 600;
    line-height: 1.4;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    vertical-align: middle;
    color: ${MUTED_FG};
    background: var(--hd-muted);
  }

  /* Reference: SectionTitle — mb-4 text-base font-semibold tracking-tight. */
  .section-title {
    margin: 0 0 16px;
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
    letter-spacing: -0.025em;
    /* GlobalStyles capitalises every heading — the reference does not. */
    text-transform: none;
    color: ${FG};
  }

  .settings-card {
    overflow: hidden;
    border: 1px solid ${BORDER};
    border-radius: 22px;
    background: ${BG};
  }

  /* Reference: Row — px-6 py-5 with a divider between rows. */
  .settings-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 20px 24px;

    & + .settings-row,
    & + .settings-block {
      border-top: 1px solid ${BORDER};
    }

    @media (max-width: 599px) {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  .row-copy {
    min-width: 0;
  }

  .row-title {
    font-size: 15px;
    line-height: 22px;
    font-weight: 600;
    color: ${FG};
  }

  .row-subtitle {
    margin: 4px 0 0;
    font-size: 14px;
    line-height: 20px;
    overflow-wrap: anywhere;
    color: ${MUTED_FG};
  }

  .row-action {
    flex-shrink: 0;
  }

  /* Reference: SoftButton — rounded-lg bg-muted px-4 py-2 text-sm font-medium */
  .soft-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border: none;
    border-radius: 14px;
    font-family: inherit;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    color: ${FG};
    background: ${MUTED};
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover:not(:disabled) {
      background: ${MUTED_70};
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &:focus-visible {
      outline: 2px solid var(--hd-ring);
      outline-offset: 2px;
    }
  }

  .settings-block {
    padding: 20px 24px;

    & + .settings-block,
    & + .settings-row {
      border-top: 1px solid ${BORDER};
    }
  }

  .block-title {
    font-size: 15px;
    line-height: 22px;
    font-weight: 600;
    color: ${FG};
  }

  /* Reference: the AI Credits bar under the plan rows. */
  .credit-row {
    margin-top: 12px;

    .credit-label {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      font-size: 14px;
      line-height: 20px;
      color: ${MUTED_FG};
    }

    .credit-track {
      height: 6px;
      margin-top: 8px;
      overflow: hidden;
      border-radius: 999px;
      background: ${MUTED};
    }

    .credit-fill {
      height: 100%;
      border-radius: 999px;
      background: ${GRADIENT};
      transition: width 0.3s ease;
    }
  }

  /* Reference: MetricRow — label left, "n used" and "n incl." right. */
  .metric-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px 0;

    & + .metric-row {
      border-top: 1px solid ${BORDER};
    }

    .metric-label {
      font-size: 15px;
      line-height: 22px;
      color: ${FG};
    }

    .metric-values {
      display: flex;
      align-items: center;
      gap: 32px;
      font-size: 14px;
      line-height: 20px;
      color: ${FG};

      .metric-total {
        width: 64px;
        text-align: right;
        color: ${MUTED_FG};
      }
    }
  }

  /* Avatar picker row. */
  .avatar-picker {
    position: relative;
    display: grid;
    place-items: center;
    overflow: hidden;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: ${MUTED};
    cursor: pointer;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    input {
      display: none;
    }

    .avatar-overlay {
      position: absolute;
      inset: 0;
      display: grid;
      place-items: center;
      color: #fff;
      background: rgba(15, 23, 42, 0.45);
      opacity: 0;
      transition: opacity 0.15s ease;
    }

    &:hover .avatar-overlay {
      opacity: 1;
    }
  }

  .avatar-identity {
    display: flex;
    align-items: center;
    gap: 16px;
    min-width: 0;
  }

  /* Existing feature components keep their own chrome; the card just hosts
     them so the section reads like the rest of the page. */
  .embedded {
    padding: 8px 4px;
  }

  /* Reference: section heads that pair a heading with an accent icon
     (Add-ons, Whitelabel). */
  .section-head-icon {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 20px;

    svg {
      color: var(--hd-primary);
    }
  }

  .section-title-sm {
    margin: 0;
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
    letter-spacing: -0.025em;
    text-transform: none;
    color: ${FG};
  }

  /* Reference: the icon tile on add-on, integration, MCP and API key rows. */
  .tile {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    width: 40px;
    height: 40px;
    border-radius: 14px;
    font-size: 14px;
    font-weight: 700;
    color: #fff;
  }

  .tile.tile-gradient {
    background: ${GRADIENT};
  }

  .tile.tile-invert {
    color: ${BG};
    background: ${FG};
  }

  .tile.tile-muted {
    color: ${FG};
    background: ${MUTED};
  }

  .list-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px 24px;

    & + .list-row {
      border-top: 1px solid ${BORDER};
    }

    @media (max-width: 599px) {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  .list-identity {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .list-name {
    font-size: 15px;
    line-height: 22px;
    font-weight: 600;
    color: ${FG};
  }

  .list-desc {
    margin: 2px 0 0;
    overflow: hidden;
    font-size: 14px;
    line-height: 20px;
    text-overflow: ellipsis;
    color: ${MUTED_FG};
  }

  /* Reference: the add-on status, rendered as a plain primary-coloured action. */
  .link-btn {
    flex-shrink: 0;
    padding: 0;
    border: none;
    font-family: inherit;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    color: var(--hd-primary);
    background: transparent;
    cursor: pointer;

    &:hover {
      opacity: 0.8;
    }
  }

  .inline-link {
    color: var(--hd-primary);
    text-underline-offset: 2px;

    &:hover {
      text-decoration: underline;
    }
  }

  /* ---- Whitelabel ---- */

  .wl-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    line-height: 22px;
    font-weight: 600;
    color: ${FG};

    svg {
      color: ${MUTED_FG};
    }
  }

  .wl-field-row {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 12px;

    @media (min-width: 640px) {
      flex-direction: row;
      align-items: center;
    }
  }

  .wl-input {
    flex: 1;
    width: 100%;
    padding: 10px 12px;
    border: 1px solid ${BORDER};
    border-radius: 14px;
    font-family: inherit;
    font-size: 14px;
    line-height: 20px;
    color: ${FG};
    background: ${BG};
    outline: none;
    transition: border-color 0.12s ease;

    &::placeholder {
      color: ${MUTED_FG};
    }

    &:focus {
      border-color: ${FG_30};
    }
  }

  .wl-hint {
    margin: 8px 0 0;
    font-size: 12px;
    line-height: 16px;
    color: ${MUTED_FG};

    code {
      padding: 1px 4px;
      border-radius: 6px;
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      background: ${MUTED};
    }
  }

  .wl-upload-grid {
    display: grid;
    grid-template-columns: 1fr;
    border-top: 1px solid ${BORDER};

    @media (min-width: 640px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  .wl-upload {
    padding: 20px 24px;
    cursor: pointer;
    border-bottom: 1px solid ${BORDER};

    @media (min-width: 640px) {
      border-bottom: none;

      & + .wl-upload {
        border-left: 1px solid ${BORDER};
      }
    }

    input {
      display: none;
    }

    .wl-upload-row {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-top: 12px;
    }

    .wl-upload-box {
      display: grid;
      place-items: center;
      overflow: hidden;
      width: 56px;
      height: 56px;
      border: 1px dashed ${BORDER};
      border-radius: 14px;
      color: ${MUTED_FG};
      background: ${MUTED};

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    .wl-upload-hint {
      font-size: 14px;
      line-height: 20px;
      color: ${MUTED_FG};
    }
  }

  .wl-accent-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 12px;

    input[type='color'] {
      width: 56px;
      height: 40px;
      padding: 2px;
      border: 1px solid ${BORDER};
      border-radius: 14px;
      background: ${BG};
      cursor: pointer;
    }

    .wl-accent-text {
      width: 128px;
      flex: none;
    }
  }

  .wl-save-row {
    display: flex;
    justify-content: flex-end;
    padding: 16px 24px;
    border-top: 1px solid ${BORDER};
  }

  /* Reference: bg-foreground text-background — an inverted button. */
  .invert-btn {
    padding: 8px 16px;
    border: none;
    border-radius: 14px;
    font-family: inherit;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    color: ${BG};
    background: ${FG};
    cursor: pointer;

    &:hover {
      opacity: 0.9;
    }
  }

  /* ---- AI Home credits ---- */

  .topup-tile {
    display: inline-grid;
    flex-shrink: 0;
    place-items: center;
    width: 40px;
    height: 40px;
    border-radius: 12px;
    color: #fff;
    background: ${GRADIENT};
  }

  .topup-amounts {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 16px;
  }

  .topup-chip {
    min-width: 64px;
    padding: 9px 16px;
    border: 1px solid ${BORDER};
    border-radius: 14px;
    font-family: inherit;
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
    color: ${FG};
    background: ${BG};
    cursor: pointer;
    transition:
      border-color 0.12s ease,
      background 0.12s ease;

    &:hover {
      background: ${MUTED};
    }

    &.is-selected {
      border-color: var(--hd-primary);
      background: color-mix(in srgb, var(--hd-primary) 8%, transparent);
    }

    &:focus-visible {
      outline: 2px solid var(--hd-ring);
      outline-offset: 2px;
    }
  }

  .topup-custom {
    flex: 0 1 120px;
    min-width: 96px;

    &.is-selected {
      border-color: var(--hd-primary);
    }
  }

  .topup-checkout {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px 16px;
    margin-top: 20px;
  }

  .topup-buy {
    padding: 10px 18px;
    border: none;
    border-radius: 14px;
    font-family: inherit;
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
    color: #fff;
    background: ${GRADIENT};
    box-shadow: var(--hd-shadow-card);
    cursor: pointer;

    &:hover:not(:disabled) {
      opacity: 0.9;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .topup-secure {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    line-height: 16px;
    color: ${MUTED_FG};
  }

  .settings-foot {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 40px;
    font-size: 12px;
    line-height: 16px;
    color: ${MUTED_FG};

    svg {
      color: var(--hd-primary);
    }
  }
`;

/** Reference: the dialog bodies behind Change name / Change password. */
export const StyledSettingsDialog = styled.div`
  ${brandTheme}
  padding: 4px 4px 0;
  font-family: var(--base-font-sans-serif);

  .dialog-title {
    margin: 0;
    font-size: 18px;
    line-height: 26px;
    font-weight: 600;
    letter-spacing: -0.015em;
    text-transform: none;
    color: ${FG};
  }

  .dialog-desc {
    margin: 4px 0 0;
    font-size: 14px;
    line-height: 20px;
    color: ${MUTED_FG};
  }

  .dialog-fields {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 20px;
  }

  .dialog-field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    span {
      font-size: 14px;
      line-height: 20px;
      font-weight: 500;
      color: ${FG};
    }
  }

  input,
  select {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid ${BORDER};
    border-radius: 10px;
    font-family: inherit;
    font-size: 14px;
    line-height: 20px;
    color: ${FG};
    background: ${BG};
    outline: none;
    transition: border-color 0.12s ease;

    &::placeholder {
      color: ${MUTED_FG};
    }

    &:focus {
      border-color: ${FG_30};
    }
  }

  .dialog-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 24px;
  }

  .dialog-cancel,
  .dialog-save {
    height: 40px;
    padding: 0 16px;
    border-radius: 14px;
    font-family: inherit;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    cursor: pointer;
  }

  .dialog-cancel {
    border: 1px solid ${BORDER};
    color: ${FG};
    background: ${BG};

    &:hover {
      background: ${MUTED};
    }
  }

  .dialog-save {
    border: none;
    color: #fff;
    background: ${GRADIENT};
    box-shadow: var(--hd-shadow-card);

    &:hover:not(:disabled) {
      opacity: 0.9;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
`;
